import { FilterOutlined } from '@ant-design/icons';
import { Form, Row, Col, Select, DatePicker, Tooltip, Button, Drawer } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../shared/components/pageHeader/PageHeader';
import { DASHBOARD_TYPE_OPTIONS, GUTTOR_SPACE } from '../../shared/constants/commonConstants';
import { Payer } from '../../shared/models/payer.model';
import ApplicationDetail from '../../shared/services/applicationDetail.service';
import { RootState } from '../../store';
import { onFilterPayer, onFilterType, reArrangeOptions } from '../crm/inPatient/service/crmUtils';
import { ReactComponent as MoreFilterIcon } from '../../assets/images/svg/More_Filter.svg';
import { getPowerBIData, setMetricsFilterFilter, setMetricsPayers,
   submitDashboardFilterSuccess } from '../../store/actions/dashboardAction';
import CompareMode from './CompareMode';
import NormalMode from './NormalMode';
import ExtraContent from './ExtraContent';
import { rearrangeMetricsDashboardFormFilters } from '../../utils/utility';
import moment from 'moment';
import { getHospDetailPayer } from '../../store/actions/authAction';

const FOOT_FALL_DASHBOARD_TYPE = 'FootfallTrends_IHX';

const FootfallTrends = (): ReactElement => {
  const [isCompareDashboard, setIsCompareDashboard] = useState(false);
  const [isSideMenuOn, setIsSideMenuOn] = useState(false);
  const [reloadPbi, setReloadPbi] = useState(0);
  const [searchForm] = Form.useForm();
  const [sideForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const appDetail = ApplicationDetail.instance;
  const groupProviders = appDetail.getGroupProviders();
  const formattedProviderList = reArrangeOptions(groupProviders, 'providerId', 'providerName');
  const configuredPayers = useSelector((state: RootState): Payer[] => state?.payerList?.allPayers || []);
  const allPayers = configuredPayers?.map((ele: Payer): any => ({ ...ele, value: ele?.uniqueIdentifier, label: ele?.name }));
  const {
    pbiData, metricsBoardFilter, isFilterApplied, hospitalWisePayer,
  } = useSelector((state: RootState): any => state?.dashboard || {});

  useEffect((): any => {
    dispatch(submitDashboardFilterSuccess({}));
    setIsLoading(true);
    isCompareDashboard
      ? dispatch(getPowerBIData('CreditBusinessComparison', onCallbackPBIData))
      : dispatch(getPowerBIData(FOOT_FALL_DASHBOARD_TYPE, onCallbackPBIData));

    if (metricsBoardFilter?.type) {
      sideForm.setFields([
        {
          name: 'type',
          value: metricsBoardFilter?.type,
          errors: [],
        },
      ]);
    }
    searchForm.setFields(rearrangeMetricsDashboardFormFilters(metricsBoardFilter));

    dispatch(setMetricsFilterFilter(metricsBoardFilter, FOOT_FALL_DASHBOARD_TYPE));

    return (): any => {
      dispatch(submitDashboardFilterSuccess({}));
    };
  }, [isCompareDashboard, metricsBoardFilter]);

  useEffect((): any => {
    if (metricsBoardFilter?.searchCenter?.length === 0) {
      dispatch(setMetricsPayers([]));
      resetFilter();
    }
  }, [metricsBoardFilter]);

  const onCallbackPBIData = (): any => { setIsLoading(false); };

  const moveToDefault = (): any => {
    setIsCompareDashboard(false);
    resetFilter();
  };

  const resetFilter = (): any => {
    dispatch(setMetricsFilterFilter({}, ''));
    searchForm.resetFields();
    sideForm.resetFields();
    setReloadPbi(reloadPbi + 1);
  };

  const onFormChange = (data: any): any => {
    setReloadPbi(reloadPbi + 1);
    const updatedFilterData = {
      ...metricsBoardFilter,
      ...data,
    };
    dispatch(setMetricsFilterFilter(updatedFilterData, FOOT_FALL_DASHBOARD_TYPE));
  };

  const openComparision = (): any => {
    searchForm.resetFields();
    sideForm.resetFields();
    setIsCompareDashboard(true);
  };

  const disabledDate = (current: any, dateType: string): any => {
    if (dateType === 'fromMonth') {
      const { toMonth } = searchForm.getFieldsValue(['toMonth']);
      return metricsBoardFilter.fromMonth && metricsBoardFilter?.toMonth ? current &&
       current > moment(toMonth).endOf('day') : current && current.valueOf() > Date.now();
    } else {
      const { fromMonth } = searchForm.getFieldsValue(['fromMonth']);
      const dateCheck = moment(fromMonth).isSame(Date.now(), 'day');
      return searchForm.getFieldsValue(['fromMonth'])?.fromMonth ? dateCheck ? moment().add(-1, 'days') >= current ||
        moment().add(1, 'month') <= current || current && current.valueOf() > Date.now() : current &&
        current.valueOf() > Date.now() || current < moment(fromMonth).endOf('day') : current && current.valueOf() > Date.now();
    }
  };

  const hospitalCenterChangeHandler = (hospitalId: any): void => {
    if (hospitalId?.length > 0) {
      dispatch(getHospDetailPayer(hospitalId));
    }
  };

  const getRightComponent = (): ReactElement => {
    if (isCompareDashboard) {
      return (
        <React.Fragment>
          <Form
            form={searchForm}
            onValuesChange={onFormChange}
          >
            <Row gutter={GUTTOR_SPACE} justify="end">
              <Col md={2} className="fsize_sm gray-text custom-lebel" style={{marginRight: '-35px'}}>Filter By:</Col>
              <Col md={8}>
                <Form.Item name="searchCenter">
                  <Select
                    placeholder="Search Center"
                    options={formattedProviderList || []}
                    allowClear={true}
                    showSearch={true}
                    mode="multiple"
                    maxTagCount="responsive"
                    style={{width: '100%'}}
                    filterOption ={onFilterPayer}

                  />
                </Form.Item>
              </Col>

              <Col md={2} className="fsize_sm gray-text custom-lebel" style={{marginRight: '-15px'}}>Time Period:</Col>
              <Col md={4}>
                <Form.Item name="fromMonth">
                  <DatePicker
                    placeholder="From"
                    allowClear={true}
                  />
                </Form.Item>
              </Col>

              <Col md={4}>
                <Form.Item name="toMonth">
                  <DatePicker
                    placeholder="To"
                    allowClear={true}
                  />
                </Form.Item>
              </Col>
              <Col md={1}>
              <Tooltip title="Reset filter" mouseEnterDelay={0.25}>
                  <Button
                    data-testid="compare-center-clear-filter"
                    onClick={resetFilter}
                    type={(isFilterApplied) ? 'primary' : 'default'}
                    icon={<FilterOutlined />}
                  />
                </Tooltip>
              </Col>
              <Col md={1} className="mr-5">
              <Button
                  type="primary"
                  icon={<MoreFilterIcon />}
                  onClick={(): any => setIsSideMenuOn(!isSideMenuOn)}
                />
              </Col>
            </Row>
          </Form>
          </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <Form
            form={searchForm}
            onValuesChange={onFormChange}
          >
            <Row gutter={GUTTOR_SPACE} justify="end">
              <Col className="fsize_sm gray-text custom-lebel">Filter By:</Col>

              {formattedProviderList?.length > 0 &&
                <Col md={4}>
                  <Form.Item name="searchCenter">
                    <Select
                      placeholder="Search Center"
                      options={formattedProviderList || []}
                      onChange={hospitalCenterChangeHandler}
                      allowClear={true}
                      showSearch={true}
                      mode="multiple"
                      maxTagCount="responsive"
                      style={{width: '100%'}}
                      filterOption ={onFilterPayer}

                    />
                  </Form.Item>
                </Col>
              }

              <Col md={4}>
                <Form.Item name="searchPayer">
                  <Select
                    placeholder="Search payer"
                    allowClear={true}
                    showSearch={true}
                    mode="multiple"
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                    options={hospitalWisePayer?.length > 0 ? hospitalWisePayer :
                      allPayers}
                      filterOption ={onFilterPayer}

                  />
                </Form.Item>
              </Col>
              <Col className="fsize_sm gray-text custom-lebel">Time Period:</Col>
              <Col>
                <Form.Item name="fromMonth">
                  <DatePicker
                    placeholder="From"
                    allowClear={true}
                    disabledDate={(e: any): any => disabledDate(e, 'fromMonth')}
                  />
                </Form.Item>
              </Col>

              <Col>
                <Form.Item name="toMonth">
                  <DatePicker
                    placeholder="To"
                    allowClear={true}
                    disabledDate={(e: any): any => disabledDate(e, 'toMonth')}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Tooltip title="Reset filter" mouseEnterDelay={0.25}>
                  <Button
                    data-testid="compare-center-clear-filter"
                    onClick={resetFilter}
                    type={(isFilterApplied) ? 'primary' : 'default'}
                    icon={<FilterOutlined />}
                  />
                </Tooltip>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<MoreFilterIcon />}
                  onClick={(): any => setIsSideMenuOn(!isSideMenuOn)}
                />
              </Col>
              {false &&
                <Col>
                  <Button
                    htmlType="button"
                    type="primary"
                    onClick={openComparision}>
                    Compare Centers
                  </Button>
                </Col>
              }
              <Col>
                <ExtraContent
                  sideForm={sideForm}
                />
              </Col>
            </Row>
          </Form>
        </React.Fragment>
      );
    }
  };

  return (
    <div className="dashboard-container">
      <PageHeader
        pageTitle={isCompareDashboard ? 'Comparison' : ''}
        rightComponent={getRightComponent}
        backUrl={isCompareDashboard ? moveToDefault : null}
        colSize={4}
      />
      {
        isCompareDashboard ?
          <CompareMode {...{
            pbiData,
            searchForm,
            sideForm,
            reloadPbi,
            isLoading,
          }} /> :
          <NormalMode {...{
            pbiData,
            searchForm,
            sideForm,
            metricsBoardFilter,
            reloadPbi,
            isLoading,
          }} />
      }
      <Drawer
        title={(<strong>More Filters</strong>)}
        placement="right"
        visible={isSideMenuOn}
        onClose={(): any => setIsSideMenuOn(!isSideMenuOn)}
        width={450}
      >
        <Form
          form={sideForm}
          layout="vertical"
          onValuesChange={onFormChange}
        >
          <Row gutter={GUTTOR_SPACE}>
            {
              isCompareDashboard &&
              <Col md={24}>
                <Form.Item name="searchPayer" label="Payor">
                  <Select
                    placeholder="Search payor"
                    allowClear={true}
                    showSearch={true}
                    mode="multiple"
                    style={{width: '100%'}}
                    maxTagCount="responsive"
                    options={allPayers || []}
                    filterOption ={onFilterPayer}

                  />
                </Form.Item>
              </Col>
            }
            <Col md={24}>
              <Form.Item name="type" label="Type">
                <Select
                  placeholder="Select Type"
                  allowClear={true}
                  showSearch={true}
                  options={DASHBOARD_TYPE_OPTIONS}
                  filterOption ={onFilterType}

                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </div>
  );
};
export default FootfallTrends;
